/**
 * La sessione di verifica e i lease, dal lato della pagina.
 *
 * **La pagina non decide niente di tutto questo.** Quanto dura una sessione,
 * quanti candidati si possono tenere, chi puo' rilasciarli: sono decisioni del
 * servizio, e questo modulo si limita a chiederle e a riportarle. Un limite
 * ricopiato qui varrebbe finche' i due numeri restano uguali, e il giorno in
 * cui divergono la differenza si vedrebbe come un rifiuto inspiegabile.
 *
 * **La scheda ha un'identita', l'utenza no.** La stessa persona puo' lavorare
 * su due schermi: sono due batch distinti, e il servizio deve poterli
 * distinguere. L'identificatore vive in `sessionStorage` e non in
 * `localStorage`: due schede devono averne due, e la memoria condivisa ne
 * darebbe uno solo.
 */
import { ask, drop } from './api'

/** Ogni trenta secondi, come dichiara il servizio. Il valore vero arriva nella
 *  risposta (`heartbeat_seconds`): questo e' il ritmo di partenza, prima che
 *  una sessione esista. */
export const HEARTBEAT_MS = 30_000

const INSTANCE = 'gridfly.client-instance'

export interface ReviewSession {
  review_session_id: string
  inspection_id: string
  owner_id: string
  client_instance_id: string
  review_epoch: number
  state: string
  last_heartbeat_at: string
  expires_at: string
  active_leases: number
  session_ttl_seconds: number
  heartbeat_seconds: number
  lease_ttl_seconds: number
  max_active_leases: number
}

export interface Lease {
  token: string
  review_session_id: string
  detection_id: string
  inspection_id: string
  holder_id: string
  state: string
  review_epoch: number
  candidate_version: number
  acquired_at: string
  expires_at: string
}

export interface Preleased {
  review_session_id: string
  leases: Lease[]
  active_leases: number
  remaining: number
}

/** Quale scheda sta lavorando. Nasce alla prima domanda e resta finche' la
 *  scheda e' aperta. */
export function clientInstance(): string {
  const known = sessionStorage.getItem(INSTANCE)
  if (known !== null && known !== '') return known
  const fresh = crypto.randomUUID()
  sessionStorage.setItem(INSTANCE, fresh)
  return fresh
}

/** Apre la sessione su una commessa, o ritrova quella di questa scheda.
 *
 * Idempotente per utente, scheda e commessa: un ricaricamento non deve
 * raddoppiare il lavoro che la persona ha in mano. */
export function openSession(inspectionId: string): Promise<ReviewSession> {
  return ask<ReviewSession>('/review-sessions', {
    inspection_id: inspectionId,
    client_instance_id: clientInstance(),
  })
}

/** Tiene viva la sessione. Un battito perso non la uccide; due si'. */
export function heartbeat(reviewSessionId: string): Promise<ReviewSession> {
  return ask<ReviewSession>(`/review-sessions/${encodeURIComponent(reviewSessionId)}/heartbeat`, {})
}

/** Chiude e restituisce il lavoro preso: senza, la commessa resta ferma fino
 *  alla scadenza per candidati che nessuno sta guardando. */
export function closeSession(reviewSessionId: string): Promise<void> {
  return drop(`/review-sessions/${encodeURIComponent(reviewSessionId)}`)
}

/** Prende in mano i prossimi candidati liberi, mai oltre il limite. */
export function prelease(
  reviewSessionId: string,
  count: number,
  detectionIds?: string[],
): Promise<Preleased> {
  return ask<Preleased>(
    `/review-sessions/${encodeURIComponent(reviewSessionId)}/candidates/prelease`,
    detectionIds === undefined ? { count } : { count, detection_ids: detectionIds },
  )
}

/** Restituisce alla coda un candidato su cui non si e' deciso niente.
 *
 * Senza questo, chi non sa decidere lo tiene fermo fino alla scadenza del lease
 * mentre un collega potrebbe guardarlo subito — ed e' anche l'unico modo di
 * liberare posto nel batch senza chiudere la sessione. */
export function releaseLease(token: string): Promise<void> {
  return drop(`/review-leases/${encodeURIComponent(token)}`)
}
